import { api, type SourceNovel, type ChapterItem, type LibraryItem, type HistoryItem, type Category } from '../api';
import { coverEl, coverGradient, qs, toast, pluralUk, escapeHtml, workHref, switchToPrettyUrl, switchToPrettyUrlSync, sourceHref, getShareUrl } from '../utils';
import { setHeroBackground } from '../img-fix';
import { mountAnimePlayer, parseAnimeSources, decodeChapterSources, hostnameOf, type AnimePlayerHandle } from './animeVideo';

const STATUS_LABELS: Record<string, string> = {
  Ongoing: 'Видається',
  Completed: 'Завершено',
  Licensed: 'Ліцензовано',
  'Publishing Finished': 'Публікацію завершено',
  Cancelled: 'Покинуто',
  OnHiatus: 'Призупинено',
  Unknown: 'Невідомо',
};

// "Переглянуто" (позначка read + врахування в лічильнику непрочитаних) —
// коли РЕАЛЬНИЙ час відтворення відео для цієї серії (не годинник сторінки)
// сумарно сягнув 22 хвилин. Рахуємо через timeupdate/pause подій нашого
// власного <video> (mountAnimePlayer/onProgress) — пауза чи неактивна
// вкладка більше НЕ накручують час. Для хостингів без екстрактора (фолбек
// на чужий <iframe>, де немає доступу до подій програвача через cross-
// origin) лишаємо старе наближення: рахуємо, лише поки вкладка видима.
const WATCH_THRESHOLD_SECONDS = 22 * 60;
// Якщо знаємо РЕАЛЬНУ тривалість відео (справжній <video>, не iframe-
// фолбек) — серію вважаємо переглянутою вже по досягненні цього відсотка
// тривалості, а не лише на 100% (це звичний UX — титри/аутро можна не
// домотувати). Для iframe-фолбеку (тривалість невідома) лишається старий
// запасний варіант — 22 хв. накопиченого часу перегляду.
const WATCHED_DURATION_RATIO = 0.92;
// Прогрес (і позиція для "Продовжити") зберігається на сервер НЕ періодично
// і НЕ на кожну паузу — лише за трьома умовами (щоб не спамити запитами,
// поки серія просто відкрита):
//   1. користувач залишає сторінку (beforeunload/pagehide/tab hidden);
//   2. користувач перемикає серію, поки була відкрита інша;
//   3. 10 хвилин без активності відтворення — зберігаємо один раз (див.
//      scheduleIdleSave/noteActivity нижче).
const IDLE_SAVE_SECONDS = 10 * 60;

function pluralSerii(n: number): string {
  return pluralUk(n, ['серія', 'серії', 'серій']);
}

export async function initAnime() {
  const pluginId = qs('plugin');
  const path = qs('path');
  const root = document.getElementById('anime-root') as HTMLElement;
  const empty = document.getElementById('empty') as HTMLElement;

  if (!pluginId || !path) {
    root.style.display = 'none';
    empty.style.display = 'flex';
    return;
  }
  switchToPrettyUrlSync(pluginId, path);

  // ---------------- кнопка "назад" (спрощено відносно novel.ts — без
  // збереження стану вкладки/сортування бібліотеки, той рівень деталізації
  // тут не запитували) ----------------
  const fromParam = qs('from');
  const FROM_TARGETS: Record<string, string> = {
    history: '/history',
    updates: '/updates',
    library: '/library',
    recommendations: '/recommendations',
  };
  const backLink = document.getElementById('novel-back-link') as HTMLAnchorElement;
  backLink.href = fromParam && FROM_TARGETS[fromParam] ? FROM_TARGETS[fromParam] : sourceHref(pluginId);
  const stickyBackLink = document.getElementById('novel-sticky-back') as HTMLAnchorElement;
  stickyBackLink.addEventListener('click', (e) => {
    e.preventDefault();
    backLink.click();
  });

  // ---------------- заголовок ----------------
  const heroBgImg = document.getElementById('hero-bgimg') as HTMLElement;
  const heroCover = document.getElementById('hero-cover') as HTMLElement;
  const genreScroll = document.getElementById('genre-scroll') as HTMLElement;
  const yearLine = document.getElementById('anime-year-line') as HTMLElement;
  const yearSpan = document.getElementById('anime-year') as HTMLElement;
  const episodesLine = document.getElementById('anime-episodes-line') as HTMLElement;
  const episodesSpan = document.getElementById('anime-episodes') as HTMLElement;
  const voiceCastSection = document.getElementById('voice-cast-section') as HTMLElement;
  const voiceCastList = document.getElementById('voice-cast-list') as HTMLElement;
  const relatedSection = document.getElementById('related-section') as HTMLElement;
  const relatedToggle = document.getElementById('related-toggle') as HTMLButtonElement;
  const relatedList = document.getElementById('related-list') as HTMLElement;
  const webviewBtn = document.getElementById('webview-btn') as HTMLAnchorElement;
  const descBox = document.getElementById('novel-desc') as HTMLElement;
  const descToggle = document.getElementById('desc-toggle') as HTMLElement;
  descToggle.addEventListener('click', () => descBox.classList.toggle('expanded'));

  // "Пов'язані твори" — випадаючий список: у згорнутому стані видно рівно 2
  // (висота вираховується по фактично відрендерених перших двох рядках, щоб
  // не залежати від точної математики відступів/шрифтів).
  let relatedExpanded = false;
  let relatedCollapsedHeight = 0;
  function collapseRelatedList(expand: boolean) {
    const rows = [...relatedList.children] as HTMLElement[];
    if (rows.length <= 2) {
      relatedList.style.maxHeight = 'none';
      relatedToggle.disabled = true;
      return;
    }
    relatedToggle.disabled = false;
    if (!relatedCollapsedHeight) {
      const containerTop = relatedList.getBoundingClientRect().top;
      relatedCollapsedHeight = Math.ceil(rows[1].getBoundingClientRect().bottom - containerTop);
    }
    relatedList.style.maxHeight = expand ? relatedList.scrollHeight + 'px' : relatedCollapsedHeight + 'px';
  }
  relatedToggle.addEventListener('click', () => {
    relatedExpanded = !relatedExpanded;
    relatedToggle.classList.toggle('expanded', relatedExpanded);
    collapseRelatedList(relatedExpanded);
  });

  function renderHeader(
    data: Pick<
      SourceNovel,
      'name' | 'cover' | 'status' | 'summary' | 'genres' | 'url' | 'voiceCastGroups' | 'releaseYear' | 'episodesLabel' | 'episodeDuration' | 'related'
    >,
    pluginLabel: string,
  ) {
    document.title = data.name;

    {
      const [from, to] = coverGradient(pluginId! + path);
      const gradient = `linear-gradient(160deg, ${from}, ${to})`;
      if (data.cover) setHeroBackground(heroBgImg, data.cover, gradient);
      else heroBgImg.style.backgroundImage = gradient;
    }
    heroCover.innerHTML = '';
    heroCover.appendChild(coverEl(data.name, data.cover, pluginId! + path));

    (document.getElementById('novel-title') as HTMLElement).textContent = data.name;

    // Там, де в novel.html — автор і художник, тут (над статусом) — Рік і
    // Серії окремими рядками (не одним рядком, і без тривалості серії — вона
    // тепер частина рядка "Серії", а не року).
    if (data.releaseYear) {
      yearSpan.textContent = data.releaseYear;
      yearLine.style.display = '';
    } else {
      yearLine.style.display = 'none';
    }
    if (data.episodesLabel) {
      episodesSpan.textContent = data.episodeDuration ? `${data.episodesLabel} · ${data.episodeDuration}` : data.episodesLabel;
      episodesLine.style.display = '';
    } else {
      episodesLine.style.display = 'none';
    }

    // Статус видання — рахується на бекенді по "n з N" (n===N → "Перекладено",
    // інакше "Перекладається"); тут лише показуємо готовий рядок.
    const statusLabel = STATUS_LABELS[data.status ?? 'Unknown'] ?? data.status ?? 'Невідомо';
    (document.getElementById('novel-status') as HTMLElement).textContent = `${statusLabel} • ${pluginLabel}`;

    const descText = document.getElementById('novel-desc-text') as HTMLElement;
    descText.textContent = data.summary || 'Опис відсутній.';
    descText.style.whiteSpace = 'pre-line'; // зберігає \n як переноси рядків

    genreScroll.innerHTML = '';
    const genres = (data.genres || '')
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean);
    for (const g of genres) {
      const pill = document.createElement('div');
      pill.className = 'genrepill';
      pill.textContent = g;
      genreScroll.appendChild(pill);
    }

    if (data.url) webviewBtn.href = data.url;

    // "Ролі озвучували" — ЛИШЕ якщо сайт поділяє акторів по командах
    // (voiceCastGroups); якщо поділу нема, розділ не показуємо взагалі.
    // Простий текст через кому, згрупований по командах — кількість акторів
    // саме НА КОМАНДУ (у дужках після назви), без загального лічильника і
    // без горизонтальної прокрутки (на відміну від жанрів).
    if (data.voiceCastGroups && data.voiceCastGroups.length) {
      voiceCastList.innerHTML = '';
      for (const group of data.voiceCastGroups) {
        const p = document.createElement('p');
        const b = document.createElement('b');
        b.textContent = `${group.team} (${group.actors.length}): `;
        p.appendChild(b);
        p.appendChild(document.createTextNode(group.actors.join(', ')));
        voiceCastList.appendChild(p);
      }
      voiceCastSection.style.display = '';
    } else {
      voiceCastSection.style.display = 'none';
    }

    // "Пов'язані твори" — випадаючий список (2 видно згорнутим).
    relatedExpanded = false;
    relatedCollapsedHeight = 0;
    relatedList.style.maxHeight = '';
    relatedToggle.classList.remove('expanded');
    if (data.related && data.related.length) {
      relatedList.innerHTML = '';
      for (const r of data.related) {
        const a = document.createElement('a');
        a.className = 'related-row';
        a.href = workHref(pluginId!, r.path, 'anime');
        a.appendChild(coverEl(r.name, r.cover, pluginId! + r.path));
        const title = document.createElement('div');
        title.className = 'related-row-title';
        title.textContent = r.name;
        a.appendChild(title);
        relatedList.appendChild(a);
      }
      relatedSection.style.display = '';
      // Вимірюємо висоту ПІСЛЯ того, як браузер реально розклав рядки (обкладинки завантажаться пізніше, але висота рядка від cover:100px відома одразу).
      requestAnimationFrame(() => collapseRelatedList(false));
    } else {
      relatedSection.style.display = 'none';
    }
  }

  // ---------------- меню (Поділитися/Оновити) + липка панель ----------------
  function setupHeaderActions(idPrefix: 'novel' | 'novel-sticky') {
    const menuBtn = document.getElementById(`${idPrefix}-menu-btn`) as HTMLButtonElement;
    const menuDropdown = document.getElementById(`${idPrefix}-menu-dropdown`) as HTMLElement;
    const menuShare = document.getElementById(`${idPrefix}-menu-share`) as HTMLButtonElement;
    const menuRefresh = document.getElementById(`${idPrefix}-menu-refresh`) as HTMLButtonElement;

    menuBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      menuDropdown.style.display = menuDropdown.style.display === 'none' ? 'flex' : 'none';
    });
    document.addEventListener('click', (e) => {
      if (menuDropdown.style.display !== 'none' && !menuDropdown.contains(e.target as Node) && e.target !== menuBtn) {
        menuDropdown.style.display = 'none';
      }
    });
    menuShare.addEventListener('click', async () => {
      menuDropdown.style.display = 'none';
      const shareTitle = document.title;
      const shareUrl = await getShareUrl(pluginId, path, shareTitle);
      if (navigator.share) {
        try {
          await navigator.share({ title: shareTitle, url: shareUrl });
        } catch {
          /* користувач скасував — нічого не робимо */
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast('Посилання скопійовано');
      } catch {
        toast('Не вдалося скопіювати посилання');
      }
    });
    menuRefresh.addEventListener('click', () => {
      menuDropdown.style.display = 'none';
      toast('Оновлюю дані аніме…');
      location.reload();
    });
  }
  setupHeaderActions('novel');
  setupHeaderActions('novel-sticky');

  const stickyBar = document.getElementById('novel-sticky-bar') as HTMLElement;
  const heroBar = document.querySelector('.novel-hero-bar') as HTMLElement | null;
  if (heroBar && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => stickyBar.classList.toggle('visible', !entry.isIntersecting), { threshold: 0 }).observe(
      heroBar,
    );
  }

  // ---------------- плагін (назва для підпису статусу) ----------------
  let pluginName = pluginId;
  try {
    const plugins = await api.plugins();
    pluginName = plugins.find((p) => p.id === pluginId)?.name ?? pluginId;
  } catch {
    /* ignore */
  }

  // ---------------- бібліотека (миттєво з кешу, якщо вже додано) ----------------
  let libraryItem: LibraryItem | undefined;
  try {
    libraryItem = await api.libraryLookup(pluginId, path);
  } catch {
    /* твору немає в бібліотеці — це нормально */
  }
  if (libraryItem?.cachedNovel) renderHeader(libraryItem.cachedNovel, pluginName);

  // ---------------- живі дані (групи перекладу + серії) ----------------
  let novel: SourceNovel;
  try {
    novel = await api.novel(pluginId, path);
  } catch (err) {
    if (libraryItem?.cachedNovel) {
      // Джерело зараз недоступне, але дані вже показані з кешу бібліотеки —
      // просто попереджаємо, серій (без живого списку) не буде.
      toast(`Не вдалося оновити дані з джерела: ${(err as Error).message}`);
      novel = { ...libraryItem.cachedNovel, path, chapters: [], groups: [] };
    } else {
      root.style.display = 'none';
      empty.style.display = 'flex';
      return;
    }
  }
  renderHeader(novel, pluginName);
  switchToPrettyUrl(pluginId!, path!, novel.name);

  const groups = novel.groups ?? [];
  const allChapters = novel.chapters ?? [];
  const readSet = new Set(novel.readChapters ?? []);

  let historyEntry: HistoryItem | undefined;
  try {
    const hist = await api.history();
    historyEntry = hist.find((h) => h.pluginId === pluginId && h.novelPath === path);
  } catch {
    /* ignore */
  }

  // =====================================================================
  // Крок 1/2/3: Озвучення чи Субтитри → команда → плеєр
  // =====================================================================
  const stepType = document.getElementById('step-type') as HTMLElement;
  const stepTeam = document.getElementById('step-team') as HTMLElement;
  const stepSource = document.getElementById('step-source') as HTMLElement;
  const stepPlayer = document.getElementById('step-player') as HTMLElement;
  const typePicker = document.getElementById('type-picker') as HTMLElement;
  const teamPicker = document.getElementById('team-picker') as HTMLElement;
  const teamBackBtn = document.getElementById('team-back-btn') as HTMLButtonElement;
  const sourceBackBtn = document.getElementById('source-back-btn') as HTMLButtonElement;
  const sourcePicker = document.getElementById('source-picker') as HTMLElement;
  const playerEl = document.getElementById('anime-player') as HTMLElement;
  const chaptersCountEl = document.getElementById('chapters-count') as HTMLElement;
  const episodesList = document.getElementById('episodes-list') as HTMLElement;
  const backToSourceBtn = document.getElementById('back-to-source-btn') as HTMLButtonElement;

  let selectedType: string | null = null;
  let selectedTeam: string | null = null;
  let selectedGroupId: string | null = null;
  // Обраний на кроці "Оберіть плеєр" бажаний хостинг (напр. "ashdi.vip") —
  // саме з нього стартуватимемо відтворення кожної серії (з фолбеком на
  // типове джерело серії, якщо в ній конкретно цього хостингу немає).
  // Використовується лише коли плеєри — це АЛЬТЕРНАТИВНІ джерела всередині
  // ОДНІЄЇ групи (див. "рівень Б" нижче).
  let preferredHost: string | null = null;
  // Кандидати-групи для поточної обраної команди, коли сам сайт розкладає
  // плеєри ОКРЕМИМИ гілками дерева (кожен плеєр — свій group.id зі своїм
  // повним списком серій, а не просто альтернативне джерело всередині
  // одного episode.sources) — див. "рівень А" нижче.
  let teamCandidates: { id: string; playerLabel: string | null }[] = [];

  /** "ashdi.vip" -> "ASHDI", "mp4upload.com" -> "MP4UPLOAD". */
  function hostLabel(host: string): string {
    return (host.split('.')[0] || host).toUpperCase();
  }

  function uniqueTypes(): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const g of groups) {
      if (!seen.has(g.type)) {
        seen.add(g.type);
        out.push(g.type);
      }
    }
    return out;
  }

  /**
   * Сайт (anitube) інколи сам розкладає плеєри окремими гілками дерева
   * перекладу — тоді group.team уже приходить із бекенду як "Робота Голосом
   * • ПЛЕЄР ASHDI", "Робота Голосом • ПЛЕЄР MOON" — тобто НЕ 2 різні
   * команди, а 1 команда ("Робота Голосом") із 2 повністю окремими group.id
   * (кожен ЗІ СВОЇМ повним списком серій під конкретний хостинг). Тут
   * розрізаємо цей суфікс і об'єднуємо такі group.id в одну пігулку команди
   * за базовою назвою — інакше користувач бачить ту саму команду в списку
   * по кілька разів підряд, по одному разу на кожен хостинг.
   */
  function splitTeamPlayer(rawTeam: string): { baseTeam: string; playerLabel: string | null } {
    const m = rawTeam.match(/^(.*?)\s*•\s*ПЛЕЄР\s+(.+)$/i);
    if (m && m[1].trim()) return { baseTeam: m[1].trim(), playerLabel: m[2].trim() };
    return { baseTeam: rawTeam, playerLabel: null };
  }

  function teamsForType(type: string): { baseTeam: string; candidates: { id: string; playerLabel: string | null }[] }[] {
    const map = new Map<string, { baseTeam: string; candidates: { id: string; playerLabel: string | null }[] }>();
    for (const g of groups) {
      if (g.type !== type) continue;
      const { baseTeam, playerLabel } = splitTeamPlayer(g.team);
      let entry = map.get(baseTeam);
      if (!entry) {
        entry = { baseTeam, candidates: [] };
        map.set(baseTeam, entry);
      }
      entry.candidates.push({ id: g.id, playerLabel });
    }
    return [...map.values()];
  }

  function showStep(step: 1 | 2 | 3 | 4) {
    stepType.style.display = step === 1 ? '' : 'none';
    stepTeam.style.display = step === 2 ? '' : 'none';
    stepSource.style.display = step === 3 ? '' : 'none';
    stepPlayer.style.display = step === 4 ? '' : 'none';
  }

  function renderTypePicker() {
    typePicker.innerHTML = '';
    for (const type of uniqueTypes()) {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'group-pill';
      pill.textContent = type;
      pill.addEventListener('click', () => {
        selectedType = type;
        renderTeamPicker();
        showStep(2);
      });
      typePicker.appendChild(pill);
    }
  }

  /** Рахує, скільки серій доступно через кожен хостинг (за всіма серіями
   * переданих серій ОДНІЄЇ групи) — і який хостинг типовий (isDefault).
   * Це "рівень Б" вибору плеєра: коли самі серії однієї групи мають
   * альтернативні джерела (episode.sources), а не окремі гілки дерева. */
  function computeHostCounts(chapters: ChapterItem[]): { countByHost: Map<string, number>; defaultHost: string | null } {
    const countByHost = new Map<string, number>();
    let defaultHost: string | null = null;
    for (const ch of chapters) {
      const sources = decodeChapterSources(ch.path);
      const seenInThisEpisode = new Set<string>();
      for (const s of sources) {
        const host = hostnameOf(s.u);
        if (!seenInThisEpisode.has(host)) {
          seenInThisEpisode.add(host);
          countByHost.set(host, (countByHost.get(host) ?? 0) + 1);
        }
        if (s.d && !defaultHost) defaultHost = host;
      }
    }
    return { countByHost, defaultHost };
  }

  function renderTeamPicker() {
    if (!selectedType) return;
    teamPicker.innerHTML = '';
    // Пігулка команди показує ЛИШЕ назву команди — без "• ПЛЕЄР ...", навіть
    // якщо це так називається у сирих даних (див. splitTeamPlayer). Назва
    // плеєра з'являється тільки на кроці "Оберіть плеєр".
    for (const tg of teamsForType(selectedType)) {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'group-pill';
      pill.textContent = tg.baseTeam;
      pill.addEventListener('click', () => {
        selectedTeam = tg.baseTeam;
        teamCandidates = tg.candidates;
        preferredHost = null;
        selectedGroupId = tg.candidates.length === 1 ? tg.candidates[0].id : null;
        renderSourceStep();
      });
      teamPicker.appendChild(pill);
    }
  }

  /**
   * Вирішує, чи потрібен крок "Оберіть плеєр" узагалі для поточного стану,
   * і показує його (або одразу переходить до відео, якщо вибору нема).
   * Пігулки цього кроку показують ЛИШЕ назву плеєра (без назви команди) —
   * команда вже видна на попередньому кроці й у заголовку.
   */
  function renderSourceStep() {
    sourcePicker.innerHTML = '';

    if (teamCandidates.length > 1) {
      // Рівень А — пігулка на кожен group.id-кандидат.
      for (const candidate of teamCandidates) {
        const chapters = allChapters.filter((c) => c.group === candidate.id);
        const { defaultHost } = computeHostCounts(chapters);
        const label = candidate.playerLabel
          ? `ПЛЕЄР ${candidate.playerLabel.toUpperCase()}`
          : defaultHost
            ? `ПЛЕЄР ${hostLabel(defaultHost)}`
            : 'Плеєр';

        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'group-pill';
        if (defaultHost) pill.classList.add('recommended');

        const title = document.createElement('span');
        title.className = 'group-pill-title';
        title.textContent = label;
        pill.appendChild(title);

        const count = chapters.length;
        const badge = document.createElement('span');
        badge.className = 'group-pill-count';
        badge.textContent = `${count} ${pluralSerii(count)}`;
        pill.appendChild(badge);

        pill.addEventListener('click', () => {
          selectedGroupId = candidate.id;
          preferredHost = null;
          enterPlayerStep();
        });
        sourcePicker.appendChild(pill);
      }
      showStep(3);
      return;
    }

    // Рівень Б — один group.id, дивимось, чи є в його серій альтернативні
    // джерела (декілька хостингів у episode.sources).
    const { countByHost, defaultHost } = computeHostCounts(groupChapters());
    const hosts = [...countByHost.keys()].sort((a, b) => (countByHost.get(b)! - countByHost.get(a)!) || a.localeCompare(b));

    if (hosts.length <= 1) {
      // Вибору фактично нема — одразу до відео, без зайвого кроку.
      enterPlayerStep();
      return;
    }

    for (const host of hosts) {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'group-pill';
      if (host === defaultHost) pill.classList.add('recommended');

      const title = document.createElement('span');
      title.className = 'group-pill-title';
      title.textContent = `ПЛЕЄР ${hostLabel(host)}`;
      pill.appendChild(title);

      const count = countByHost.get(host)!;
      const badge = document.createElement('span');
      badge.className = 'group-pill-count';
      badge.textContent = `${count} ${pluralSerii(count)}`;
      pill.appendChild(badge);

      pill.addEventListener('click', () => {
        preferredHost = host;
        enterPlayerStep();
      });
      sourcePicker.appendChild(pill);
    }
    showStep(3);
  }

  /** Чи має сенс кнопка "повернутись до вибору плеєра" на кроці відео —
   * тобто чи справді є з чого обирати для поточної команди (перевіряє й
   * рівень А, і Б). */
  function hasPlayerChoice(): boolean {
    if (teamCandidates.length > 1) return true;
    return computeHostCounts(groupChapters()).countByHost.size > 1;
  }

  teamBackBtn.addEventListener('click', () => {
    selectedType = null;
    showStep(1);
  });

  sourceBackBtn.addEventListener('click', () => {
    selectedTeam = null;
    selectedGroupId = null;
    teamCandidates = [];
    if (selectedType) renderTeamPicker();
    showStep(2);
  });

  // Єдина кнопка на кроці відео (Lucide "undo-2"). Якщо для цієї команди
  // справді є вибір плеєра — повертає до кроку "Оберіть плеєр". Якщо ж у
  // команди лише ОДИН плеєр (а отже кроку "Оберіть плеєр" для неї взагалі
  // не існувало — його пропустили напряму до відео), кнопка веде на крок
  // назад ще далі — до вибору команди.
  backToSourceBtn.addEventListener('click', () => {
    if (hasPlayerChoice()) {
      renderSourceStep();
    } else {
      selectedTeam = null;
      selectedGroupId = null;
      teamCandidates = [];
      if (selectedType) renderTeamPicker();
      showStep(2);
    }
  });

  function groupChapters(): ChapterItem[] {
    if (!selectedGroupId) return [];
    return allChapters.filter((c) => c.group === selectedGroupId);
  }

  function episodeStatus(ch: ChapterItem): 'read' | 'progress' | 'unread' {
    if (readSet.has(ch.path)) return 'read';
    if (historyEntry && historyEntry.chapterPath === ch.path && historyEntry.progress > 0 && historyEntry.progress < 100) {
      return 'progress';
    }
    return 'unread';
  }

  // ---------------- Lucide "eye" (https://lucide.dev/icons/eye) — значок
  // переглянутості на самій пігулці серії (та сама заливка кольором, що й
  // текст, без окремого фону). ----------------
  const EYE_ICON_SVG =
    '<svg class="episode-chip-eye" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>' +
    '<circle cx="12" cy="12" r="3"/>' +
    '</svg>';

  // ---------------- довге натискання на серію одразу перемикає
  // прочитано/непрочитано (без будь-якого меню виділення) — короткий тап,
  // як і раніше, відкриває серію в плеєрі. ----------------
  function attachEpisodeChipInteractions(el: HTMLElement, chPath: string) {
    let timer: number | undefined;
    let moved = false;
    let longPressFired = false;

    el.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      moved = false;
      timer = window.setTimeout(() => {
        if (!moved) {
          longPressFired = true;
          void toggleEpisodeWatched(chPath);
        }
      }, 450);
    });
    el.addEventListener('pointermove', (e: PointerEvent) => {
      if (Math.abs(e.movementX) + Math.abs(e.movementY) > 6) moved = true;
    });
    const clearTimer = () => {
      if (timer) window.clearTimeout(timer);
    };
    el.addEventListener('pointerup', clearTimer);
    el.addEventListener('pointerleave', clearTimer);
    el.addEventListener('contextmenu', (e) => e.preventDefault());

    el.addEventListener('click', () => {
      if (longPressFired) {
        longPressFired = false;
        return;
      }
      const ch = groupChapters().find((c) => c.path === chPath);
      if (ch) loadEpisode(ch, el);
    });
  }

  async function toggleEpisodeWatched(chPath: string) {
    const nowWatched = !readSet.has(chPath);
    const chapterNumber = groupChapters().find((c) => c.path === chPath)?.chapterNumber;
    try {
      if (nowWatched) {
        await api.markChaptersRead({ pluginId: pluginId!, novelPath: path!, chapters: [{ path: chPath, chapterNumber }] });
        readSet.add(chPath);
      } else {
        await api.markChaptersUnread({ pluginId: pluginId!, novelPath: path!, chapters: [{ path: chPath, chapterNumber }] });
        readSet.delete(chPath);
        if (historyEntry && historyEntry.chapterPath === chPath) {
          historyEntry.progress = 0;
          historyEntry.positionSeconds = undefined;
          historyEntry.durationSeconds = undefined;
        }
      }
      renderEpisodesList();
    } catch (err) {
      toast((err as Error).message);
    }
  }

  function renderEpisodesList() {
    const list = groupChapters();
    chaptersCountEl.textContent = `${list.length} ${pluralSerii(list.length)}`;
    // Кнопка ("undo-2") тепер завжди видима: якщо для команди є вибір
    // плеєра — веде туди, інакше (лише 1 плеєр) — на крок вибору команди.
    backToSourceBtn.style.display = '';
    backToSourceBtn.title = hasPlayerChoice() ? 'До вибору плеєра' : 'До вибору команди';
    backToSourceBtn.setAttribute('aria-label', backToSourceBtn.title);

    episodesList.innerHTML = '';
    for (const ch of list) {
      const watched = readSet.has(ch.path);
      const chip = document.createElement('div');
      chip.className = 'episode-chip' + (watched ? ' watched' : '');
      chip.dataset.path = ch.path;
      chip.innerHTML = `<span class="episode-chip-title">${escapeHtml(ch.name)}</span>${EYE_ICON_SVG}`;
      if (currentChapter && ch.path === currentChapter.path) chip.classList.add('active');
      attachEpisodeChipInteractions(chip, ch.path);
      episodesList.appendChild(chip);
    }
  }

  /**
   * Точний "сирий" group.team ОБРАНОЇ конкретної групи (за selectedGroupId) —
   * а НЕ базова назва команди (selectedTeam/tg.baseTeam), яку показуємо в
   * пігулках. Коли команда розкладена на кілька дзеркал-плеєрів (кожне —
   * своя group.id зі своїм ПОВНИМ списком серій, напр. "Робота Голосом •
   * ПЛЕЄР ASHDI" і "Робота Голосом • ПЛЕЄР MOON"), базова назва в обох
   * однакова — тож зберігати треба саме повний рядок ЦІЄЇ конкретної групи,
   * інакше сервер (src/lib/chapters.ts, matchedAnimeGroupIds) не зможе
   * відрізнити один плеєр від іншого і порахує серії обох одразу (сума).
   */
  function resolvedAnimeSelectionTeam(): string | null {
    if (!selectedGroupId) return selectedTeam;
    return groups.find((g) => g.id === selectedGroupId)?.team ?? selectedTeam;
  }

  function enterPlayerStep() {
    renderEpisodesList();
    showStep(4);
    // Зберігаємо ОСТАТОЧНО обраний переклад (озвучення/субтитри + команда —
    // точний рядок КОНКРЕТНОЇ обраної групи, resolvedAnimeSelectionTeam())
    // за твором бібліотеки — не лише для щойно доданих (де це вже сталось
    // при "Додати"), а й для тих, що вже в бібліотеці без цього вибору
    // (найчастіше — anitube-синхронізовані: sync додає їх напряму, без
    // проходження цього майстра). Без цього animeSelection лишається
    // порожнім назавжди, і chapterCount на сервері (src/routes/plugins.ts,
    // src/routes/updates.ts) рахується сумою серій ПО ВСІХ перекладах
    // одразу, а не лише обраного — саме тому бібліотека показувала завищену
    // кількість серій. Пишемо лише коли вибір справді змінився (не смикати
    // сервер повторно на кожен вхід у вже узгоджений стан).
    const resolvedTeam = resolvedAnimeSelectionTeam();
    if (
      libraryItem &&
      selectedType &&
      resolvedTeam &&
      (libraryItem.animeSelection?.type !== selectedType || libraryItem.animeSelection?.team !== resolvedTeam)
    ) {
      const savedType = selectedType;
      const savedTeam = resolvedTeam;
      api
        .setAnimeSelection(libraryItem.id, { type: savedType, team: savedTeam })
        .then(() => {
          if (libraryItem) libraryItem.animeSelection = { type: savedType, team: savedTeam };
        })
        .catch(() => {
          /* не критично — наступний вхід на сторінку спробує ще раз */
        });
    }
    // "Остання переглянута серія" — НЕ те саме, що historyEntry: історія
    // фіксує лише позицію в плеєрі (де зупинився всередині конкретної
    // серії), а не оновлюється взагалі, якщо серію позначили переглянутою
    // через список серій (довге натискання) чи масово з бібліотеки —
    // саме тому скрол і не спрацьовував у цих випадках, historyEntry
    // просто не було на що вказувати.
    // Тому спершу пробуємо historyEntry (найточніше, коли серію справді
    // дивились у плеєрі — знає навіть недодивлену), а якщо для цього
    // перекладу її нема — беремо ОСТАННЮ за порядком серію зі значком "переглянуто"
    // (readSet), тобто останню, позначену прочитаною/переглянутою.
    const list = groupChapters();
    let resumeChapter = historyEntry ? list.find((c) => c.path === historyEntry!.chapterPath) : undefined;
    if (!resumeChapter) {
      for (let i = list.length - 1; i >= 0; i--) {
        if (readSet.has(list[i].path)) {
          resumeChapter = list[i];
          break;
        }
      }
    }
    playerEl.innerHTML = '<p class="anime-player-hint">Оберіть серію зі списку нижче.</p>';
    if (resumeChapter) {
      const chip = episodesList.querySelector<HTMLElement>(`[data-path="${CSS.escape(resumeChapter.path)}"]`);
      // Список серій прокручується ГОРИЗОНТАЛЬНО (.episodes-scroll,
      // overflow-x:auto) — тож потрібен саме inline-осі 'center', а не
      // block (той стосується вертикальної прокрутки сторінки й тут
      // нічого не робить). Крім того, showStep(4) щойно зробив цей блок
      // видимим у цьому ж синхронному виклику — браузер ще не встиг
      // порахувати розкладку, тож scrollIntoView одразу міг обчислити
      // позицію як "уже видно" (0 ширини) і не прокрутити. rAF чекає
      // кадр після цього showStep(4), щоб розкладка вже була готова.
      requestAnimationFrame(() => {
        chip?.scrollIntoView({ block: 'nearest', inline: 'center' });
      });
    }
  }


  // ---------------- плеєр (inline, без переходу/нової вкладки) + прогрес ----------------
  let currentChapter: ChapterItem | null = null;
  let activeRow: HTMLElement | null = null;
  // Накопичені РЕАЛЬНІ секунди перегляду поточної серії (не годинник
  // сторінки) — приходять з mountAnimePlayer.onProgress (timeupdate/pause
  // реального <video>), або, для iframe-фолбеку без доступу до подій чужого
  // плеєра, наближено — по секунді за тік, поки вкладка видима.
  let accumulatedSeconds = 0;
  let lastPersistedSeconds = 0;
  // Поточна позиція/тривалість відео в секундах (для збереження й показу
  // кнопки "Продовжити" за наступного відвідування). undefined для
  // iframe-фолбеку — там позиція невідома.
  let currentPositionSeconds: number | undefined;
  let currentDurationSeconds: number | undefined;
  let tickHandle: number | undefined;
  let idleTimer: number | undefined;
  // Умова 4 (разовий тригер, не потік): щойно прогрес ЦІЄЇ серії вперше
  // перетнув поріг "переглянуто" — зберігаємо одразу, ОДИН раз, щоб галочка
  // в списку з'явилась без затримки до наступного leave/switch/idle.
  // Скидається в loadEpisode() при переході на кожну нову серію.
  let watchedThresholdSaved = false;
  // handle змонтованого відеоплеєра — обов'язково destroy() ПЕРЕД тим, як
  // монтувати плеєр для іншої серії, інакше попередній hls.js-інстанс
  // лишиться працювати вхолосту.
  let playerHandle: AnimePlayerHandle | null = null;

  function markRowStatus(chPath: string, status: 'read' | 'progress') {
    const chip = episodesList.querySelector<HTMLElement>(`[data-path="${CSS.escape(chPath)}"]`);
    if (!chip) return;
    // Пігулка серії показує лише прочитано/непрочитано (значок ока) — без
    // окремого індикатора часткового прогресу, тож для 'progress' просто
    // нічого не змінюємо (лишається "непереглянуто", доки не дійде до 100%).
    if (status === 'read') chip.classList.add('watched');
  }

  /** Відсоток "перегляду" для розв'язання "переглянуто/ні". Пріоритет —
   * реальна тривалість відео (є лише коли витяг у стандартний <video>
   * вдався, напр. ashdi): 92%+ від неї = 100% (переглянуто). Якщо
   * тривалість невідома (iframe-фолбек чужого плеєра, де немає доступу до
   * його подій паузи/часу) — стара евристика по накопиченому часу перегляду. */
  function computeWatchedPct(): number {
    if (currentDurationSeconds && currentDurationSeconds > 5 && currentPositionSeconds !== undefined) {
      const ratio = currentPositionSeconds / currentDurationSeconds;
      // 92%+ реальної тривалості — вважаємо переглянутим (100%); нижче —
      // показуємо РЕАЛЬНИЙ відсоток перегляду, без штучного масштабування.
      if (ratio >= WATCHED_DURATION_RATIO) return 100;
      return Math.min(99, Math.round(ratio * 100));
    }
    return Math.min(100, Math.round((accumulatedSeconds / WATCH_THRESHOLD_SECONDS) * 100));
  }

  function persistProgress(final = false) {
    if (!currentChapter || !pluginId) return;
    // Фіксуємо серію ЛОКАЛЬНО в момент виклику: до моменту, коли прийде
    // відповідь сервера, loadEpisode() може вже перемкнути currentChapter
    // на нову серію (див. виклик persistProgress(true) у loadEpisode перед
    // `currentChapter = ch`). Якщо в .then() читати глобальну currentChapter
    // напряму, readSet/markRowStatus помилково позначать переглянутою ВЖЕ
    // НОВУ серію замість тієї, для якої рахувався pct — саме це й давало
    // візуальний глюк "переглянуто" на щойно відкритій серії до рефрешу.
    const chapterAtCall = currentChapter;
    lastPersistedSeconds = accumulatedSeconds;
    const pct = computeWatchedPct();
    api
      .saveProgress({
        pluginId,
        novelPath: path!,
        novelName: novel.name,
        cover: novel.cover,
        chapterPath: chapterAtCall.path,
        chapterName: chapterAtCall.name,
        chapterNumber: chapterAtCall.chapterNumber,
        progress: pct,
        positionSeconds: currentPositionSeconds,
        durationSeconds: currentDurationSeconds,
      })
      .then(() => {
        // "Переглянуто" (readSet) виставляється ЛИШЕ для ChapterItem.path
        // ТІЄЇ серії, для якої рахувався pct (chapterAtCall) — той самий
        // номер серії в ІНШІЙ команді має власний, окремий path і не
        // зачіпається, і поточна відкрита серія (currentChapter) теж не
        // зачіпається, якщо це вже інший запис.
        if (pct >= 100) {
          readSet.add(chapterAtCall.path);
          markRowStatus(chapterAtCall.path, 'read');
        } else if (!final) {
          markRowStatus(chapterAtCall.path, 'progress');
        }
      })
      .catch(() => {
        /* збережеться наступного тіку — не критично */
      });
  }

  function stopWatchTimer(flush: boolean) {
    if (tickHandle !== undefined) {
      window.clearInterval(tickHandle);
      tickHandle = undefined;
    }
    if (idleTimer !== undefined) {
      window.clearTimeout(idleTimer);
      idleTimer = undefined;
    }
    if (flush && currentChapter && (accumulatedSeconds !== lastPersistedSeconds || currentPositionSeconds !== undefined)) {
      persistProgress(true);
    }
  }

  // Умова 3 ("10 хвилин нічого не робив — запам'ятати один раз"): таймер
  // перезапускається на кожну "активність" (реальний прогрес відтворення чи
  // тік фолбек-лічильника, поки вкладка видима) і, якщо жодної активності
  // за IDLE_SAVE_SECONDS не було, зберігає прогрес ОДИН раз. Далі таймер
  // свідомо НЕ перезапускається сам собою — лишається неактивним, доки
  // користувач знову щось не зробить (тоді noteActivity() заведе його
  // заново) — інакше довге "нічого не робив" накручувало б збереження
  // кожні 10 хв безкінечно.
  function scheduleIdleSave() {
    if (idleTimer !== undefined) window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => {
      idleTimer = undefined;
      persistProgress();
    }, IDLE_SAVE_SECONDS * 1000);
  }

  function noteActivity() {
    if (!currentChapter) return;
    scheduleIdleSave();
  }

  // Періодичний тік: для iframe-фолбеку (playerHandle.getPosition() === null,
  // немає доступу до подій чужого плеєра) наближено рахує секунду перегляду
  // за тік, поки вкладка видима — так само, як і раніше. Для РЕАЛЬНОГО
  // відео (hls/mp4) нічого не рахує (це вже робить onProgress) і на
  // сервер тут (окрім разового умови-4 тригера нижче) НІЧОГО не зберігає —
  // лише рахує накопичений час локально (збереження — за умовами 1–3 вище).
  function startWatchTimer() {
    tickHandle = window.setInterval(() => {
      if (!currentChapter) return;
      const isRealVideo = playerHandle?.getPosition() !== null;
      if (!isRealVideo) {
        if (document.visibilityState !== 'visible') return;
        accumulatedSeconds++;
        noteActivity();
        // Умова 4 для iframe-фолбеку — той самий разовий тригер, що й у
        // onProgress реального <video> (там же й лежить основна умова-4
        // логіка), просто на основі накопиченого часу замість тривалості.
        if (!watchedThresholdSaved && computeWatchedPct() >= 100) {
          watchedThresholdSaved = true;
          persistProgress();
        }
      }
    }, 1000);
  }
  startWatchTimer();

  // Умова 1 ("користувач вийшов зі сторінки під час перегляду"): і
  // beforeunload/pagehide (закриття вкладки/навігація geть), і
  // visibilitychange→hidden (згортання/перемикання додатку на мобільному,
  // де beforeunload часто взагалі не встигає спрацювати) — усі означають
  // "залишив сторінку", а не рутинну паузу відео, тож саме тут (і НЕ на
  // кожній 'pause' відеоелемента) робимо єдиний фінальний запит.
  window.addEventListener('beforeunload', () => stopWatchTimer(true));
  window.addEventListener('pagehide', () => stopWatchTimer(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') persistProgress(true);
  });

  async function loadEpisode(ch: ChapterItem, row?: HTMLElement) {
    // Фіксуємо накопичений час ПОПЕРЕДНЬОЇ серії перед перемиканням.
    if (currentChapter && currentChapter.path !== ch.path) persistProgress(true);

    currentChapter = ch;
    activeRow?.classList.remove('active');
    activeRow = row ?? episodesList.querySelector<HTMLElement>(`[data-path="${CSS.escape(ch.path)}"]`);
    activeRow?.classList.add('active');

    // Відновлюємо накопичений час перегляду й позицію для "Продовжити" з
    // історії, якщо це та сама серія (той самий переклад/команда), що вже
    // там записана — інакше рахуємо з нуля (інша команда на той самий номер
    // серії — це вже ІНШИЙ ChapterItem.path, тож жодного "перетікання" між
    // командами тут статися не може).
    const sameChapterInHistory = historyEntry && historyEntry.chapterPath === ch.path ? historyEntry : null;
    accumulatedSeconds = sameChapterInHistory ? Math.round((sameChapterInHistory.progress / 100) * WATCH_THRESHOLD_SECONDS) : 0;
    lastPersistedSeconds = accumulatedSeconds;
    const resumeSeconds = sameChapterInHistory?.positionSeconds;
    currentPositionSeconds = resumeSeconds;
    currentDurationSeconds = sameChapterInHistory?.durationSeconds;
    // Якщо серія вже позначена переглянутою з попереднього разу — поріг вже
    // збережений на сервері, повторно на старті смикати його не треба.
    watchedThresholdSaved = (sameChapterInHistory?.progress ?? 0) >= 100;

    playerHandle?.destroy();
    playerHandle = null;
    playerEl.innerHTML = '<p class="anime-player-hint">Завантаження плеєра…</p>';
    // Захист від "запізнілих" подій старого (вже знищеного) плеєра: нова
    // серія, для якої монтується ЦЕЙ плеєр, фіксується тут і звіряється в
    // onProgress — якщо на момент виклику колбека currentChapter вже вказує
    // на ІНШУ серію (користувач перемкнувся ще раз), подію ігноруємо,
    // замість того щоб затирати нею стан нової серії.
    const chapterForThisMount = ch;
    try {
      const { content } = await api.chapter(pluginId!, ch.path, path!);
      const sources = content ? parseAnimeSources(content) : null;
      if (sources && sources.length) {
        // Витягуємо пряме посилання на відео з чужого плеєра (ashdi/tortuga/
        // mp4upload/...) і показуємо звичайним <video> (+ hls.js для HLS) —
        // так само, як це було в прототипі anitube-web, а не чужим <iframe>.
        playerHandle = mountAnimePlayer(playerEl, sources, {
          preferredHost,
          resumeSeconds,
          onProgress: (p) => {
            if (currentChapter !== chapterForThisMount) return;
            currentPositionSeconds = p.currentTime;
            currentDurationSeconds = p.duration || currentDurationSeconds;
            if (p.watchedDeltaSeconds > 0) {
              // Триває активне відтворення — рахуємо локально й заводимо
              // 10-хвилинний таймер бездіяльності наново. watchedDeltaSeconds
              // === 0 (подія 'pause', зокрема й технічна пауза з
              // stopPlayback() при перемиканні джерела/якості) тут навмисно
              // НІЧОГО на сервер не зберігає — це саме те "постійні запити,
              // навіть якщо відео ще не почав", про яке йшлося: раніше
              // будь-яка пауза одразу слала запит.
              accumulatedSeconds += p.watchedDeltaSeconds;
              noteActivity();
            }
            // Умова 4: разовий запит одразу по факту перетину порогу
            // "переглянуто" — не потік, спрацьовує щонайбільше раз на серію
            // (дальші тіки/паузи так само нічого не шлють, як і раніше).
            if (!watchedThresholdSaved && computeWatchedPct() >= 100) {
              watchedThresholdSaved = true;
              persistProgress();
            }
          },
        });
      } else {
        playerEl.innerHTML = content || '<p class="anime-player-hint">Не вдалося завантажити плеєр.</p>';
      }
    } catch (err) {
      playerEl.innerHTML = `<p class="anime-player-hint">Помилка завантаження: ${escapeHtml((err as Error).message)}</p>`;
    }
  }

  // ---------------- кнопка "Додати"/"У бібліотеці" ----------------
  // Та сама поведінка, що й на сторінці твору (novel.ts): короткий клік
  // додає/вилучає, довге утримання (лише коли твір уже в бібліотеці)
  // відкриває вибір/зміну категорії. Єдина різниця з novel.ts — тут
  // додавання можливе лише після вибору перекладу й команди (без цього
  // немає animeSelection, з яким твір узагалі можна зберегти).
  const libraryBtn = document.getElementById('library-btn') as HTMLButtonElement;
  function paintLibraryBtn(inLib: boolean) {
    libraryBtn.classList.toggle('active', inLib);
    (libraryBtn.querySelector('span') as HTMLElement).textContent = inLib ? 'У бібліотеці' : 'Додати';
  }
  paintLibraryBtn(!!libraryItem);

  async function openLibraryCategoryOverlay(mode: 'add' | 'change') {
    let categories: Category[] = [];
    try {
      categories = await api.categories('anime');
    } catch {
      categories = [];
    }

    if (!categories.length) {
      if (mode === 'add') await commitAddToLibrary(undefined);
      else toast('Спершу створіть категорію в бібліотеці');
      return;
    }

    const overlay = document.getElementById('library-category-overlay') as HTMLElement;
    const title = document.getElementById('library-category-title') as HTMLElement;
    const list = document.getElementById('library-category-list') as HTMLElement;
    const applyBtn = document.getElementById('library-category-apply') as HTMLButtonElement;
    const closeBtn = document.getElementById('library-category-close') as HTMLButtonElement;

    title.textContent = mode === 'add' ? 'Додати до бібліотеки' : 'Змінити категорію';
    applyBtn.textContent = mode === 'add' ? 'Додати' : 'Зберегти';

    const preselected = mode === 'change' ? libraryItem?.categoryIds[0] : undefined;

    list.innerHTML = '';
    const uncatRow = document.createElement('label');
    uncatRow.className = 'assign-cat-row';
    const uncatRadio = document.createElement('input');
    uncatRadio.type = 'radio';
    uncatRadio.name = 'library-category-radio';
    uncatRadio.value = '';
    uncatRadio.checked = !preselected;
    const uncatSpan = document.createElement('span');
    uncatSpan.textContent = 'Типово';
    uncatRow.appendChild(uncatRadio);
    uncatRow.appendChild(uncatSpan);
    list.appendChild(uncatRow);

    for (const c of categories) {
      const row = document.createElement('label');
      row.className = 'assign-cat-row';
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'library-category-radio';
      radio.value = c.id;
      radio.checked = preselected === c.id;
      const span = document.createElement('span');
      span.textContent = c.name;
      row.appendChild(radio);
      row.appendChild(span);
      list.appendChild(row);
    }

    overlay.style.display = 'flex';

    const close = () => (overlay.style.display = 'none');
    closeBtn.onclick = close;
    overlay.onclick = (e) => {
      if (e.target === overlay) close();
    };
    applyBtn.onclick = async () => {
      const checked = list.querySelector<HTMLInputElement>('input[name="library-category-radio"]:checked');
      const chosenId = checked?.value || undefined;
      close();
      if (mode === 'add') await commitAddToLibrary(chosenId);
      else await commitCategoryChange(chosenId);
    };
  }

  /** Додає твір до бібліотеки (з обраним перекладом/командою) і (якщо обрано) одразу призначає категорію. */
  async function commitAddToLibrary(categoryId: string | undefined) {
    if (!selectedType || !selectedTeam) {
      toast('Спершу оберіть переклад (озвучення/субтитри та команду)');
      return;
    }
    const resolvedTeam = resolvedAnimeSelectionTeam() ?? selectedTeam;
    try {
      const res = await api.addToLibrary({
        pluginId: pluginId!,
        path: path!,
        name: novel.name,
        cover: novel.cover,
        animeSelection: { type: selectedType, team: resolvedTeam },
      });
      if (categoryId) {
        try {
          await api.assignLibraryCategories([res.id], [categoryId], []);
        } catch (err) {
          toast((err as Error).message);
        }
      }
      libraryItem = { id: res.id, categoryIds: categoryId ? [categoryId] : [] } as LibraryItem;
      paintLibraryBtn(true);
      toast('Додано до бібліотеки');
    } catch (err) {
      toast((err as Error).message);
    }
  }

  /** Змінює категорію твору, який уже в бібліотеці, — замінює попередню на нову (або на "Типово"). */
  async function commitCategoryChange(categoryId: string | undefined) {
    if (!libraryItem) return;
    const add = categoryId ? [categoryId] : [];
    const remove = (libraryItem.categoryIds ?? []).filter((id) => id !== categoryId);
    try {
      await api.assignLibraryCategories([libraryItem.id], add, remove);
      libraryItem.categoryIds = categoryId ? [categoryId] : [];
      toast('Категорію змінено');
    } catch (err) {
      toast((err as Error).message);
    }
  }

  // Довге утримання (лише коли твір уже в бібліотеці) відкриває зміну
  // категорії; звичайний "click", що браузер усе одно шле по відпусканню
  // пальця після довгого утримання, гаситься прапорцем longPressFired —
  // інакше він одразу ж додатково вилучив би щойно змінений твір.
  let pressTimer: number | undefined;
  let moved = false;
  let longPressFired = false;

  libraryBtn.addEventListener('pointerdown', (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (!libraryBtn.classList.contains('active')) return;
    moved = false;
    pressTimer = window.setTimeout(() => {
      if (!moved) {
        longPressFired = true;
        void openLibraryCategoryOverlay('change');
      }
    }, 450);
  });
  libraryBtn.addEventListener('pointermove', (e: PointerEvent) => {
    if (Math.abs(e.movementX) + Math.abs(e.movementY) > 6) moved = true;
  });
  const clearPressTimer = () => {
    if (pressTimer) window.clearTimeout(pressTimer);
  };
  libraryBtn.addEventListener('pointerup', clearPressTimer);
  libraryBtn.addEventListener('pointerleave', clearPressTimer);
  libraryBtn.addEventListener('contextmenu', (e) => e.preventDefault());

  libraryBtn.addEventListener('click', async () => {
    if (longPressFired) {
      longPressFired = false;
      return;
    }
    if (libraryItem) {
      try {
        await api.removeFromLibrary(libraryItem.id);
        libraryItem = undefined;
        paintLibraryBtn(false);
        toast('Видалено з бібліотеки');
      } catch (err) {
        toast((err as Error).message);
      }
      return;
    }
    await openLibraryCategoryOverlay('add');
  });

  // ---------------- стартовий стан кроків ----------------
  if (!groups.length) {
    stepType.style.display = 'none';
    stepTeam.style.display = 'none';
    stepPlayer.style.display = '';
    chaptersCountEl.textContent = '0 серій';
    backToSourceBtn.style.display = 'none';
    playerEl.innerHTML = '<p class="anime-player-hint">Для цього тайтла не знайдено жодного перекладу.</p>';
  } else if (libraryItem?.animeSelection && teamsForType(libraryItem.animeSelection.type).some((tg) => tg.baseTeam === splitTeamPlayer(libraryItem!.animeSelection!.team).baseTeam)) {
    // Твір у бібліотеці й переклад вже обрано раніше (пункт 6) — одразу
    // переходимо до кроку відео (або "Оберіть плеєр", якщо для цієї
    // команди їх декілька — раніше збережений вибір не уточнює, який саме).
    // Пігулки кроків 1 і 2 все одно рендеримо (хоч і не показуємо) — інакше
    // кнопки "назад" ведуть на порожній, ще ніколи не намальований крок.
    renderTypePicker();
    selectedType = libraryItem.animeSelection.type;
    renderTeamPicker();
    const tg = teamsForType(selectedType).find((t) => t.baseTeam === splitTeamPlayer(libraryItem!.animeSelection!.team).baseTeam)!;
    selectedTeam = tg.baseTeam;
    teamCandidates = tg.candidates;
    selectedGroupId = tg.candidates.length === 1 ? tg.candidates[0].id : null;
    renderSourceStep();
  } else {
    renderTypePicker();
    showStep(1);
  }
}
